<template>
  <component is="style" v-if="state.elementStyling" type="text/css">
    {{ state.elementStyling.hoverStyling }}
  </component>

  <div class="lf-button" :style="state.elementStyling.align" :class="{ [`${state.buttonStyle}`]: true }">
    <UIButton
      :id="state.buttonId"
      class="lf-button__element"
      :is-loading="isDisabled"
      :button-style="state.buttonStyle"
      :style="buttonInlineStyles"
      :is-disabled="isDisabled"
      :prevent-stop="state.action?.state?.type"
      @click="onButtonClick($event)"
    >
      <RichTextRenderer class="content__item-action-text" :html="state?.label ?? ''" tag="span" />
    </UIButton>
  </div>
</template>

<script lang="ts">
import { computed, CSSProperties, defineComponent, onMounted, PropType, ref, watch } from 'vue';
import { AddonLfButtonModel } from '@/components/addons/lf-button/Model';
import { useStore } from '@/store';
import { ActionType, KeyBinding } from '@/typings/enums/enums';
import { FormDisableDefaultBehavior } from '@/exceptions/FormDisableDefaultBehavior';
import { capitalizeFirstLetter, isSsr } from '@/utilities/Utilities';
import { setFormReplacementTags, submitForm } from '@/utilities/Registration';
import { AddonRegistrationModel } from '../registration/Model';
import RichTextRenderer from '@/components/RichTextRenderer.vue';
import useFontObserver from '@/hooks/useFontObserver';
import UIButton from '@/components/ui/UIButton.vue';

export default defineComponent({
  name: 'AddonLfButton',
  components: { RichTextRenderer, UIButton },
  inheritAttrs: false,
  props: {
    model: {
      type: Object as PropType<AddonLfButtonModel>,
      required: true
    }
  },
  setup(props) {
    const state = (props.model as AddonLfButtonModel).state;

    const isFormSubmit = ref(false);

    const isDisabled = computed(() => {
      return isFormSubmit.value || state.action?.state.evaluating;
    });

    const onButtonClick = async (event?: Event) => {
      // We should only stop propagation if we actually have an action.
      if (event && state.action && state.action.state) {
        event.stopPropagation();
      }

      let defaultBehavior = true;

      // We should only attempt to submit registration forms if it's action is manipulating with the flow.
      if (
        state.action?.state.type &&
        [ActionType.FORCE_SKIP_REGISTRATION, ActionType.GOTO_FLOW_PAGE].includes(state.action.state.type)
      ) {
        const hasRegistrationAddon = props.model.getSection().getAddons<AddonRegistrationModel>('registration');

        if (hasRegistrationAddon.length > 0 && state.action?.state.type === ActionType.GOTO_FLOW_PAGE) {
          const section = props.model.getSection();
          isFormSubmit.value = true;
          try {
            await submitForm(section);
            setFormReplacementTags();
          } catch (e) {
            if (e instanceof FormDisableDefaultBehavior) {
              defaultBehavior = e.allowContinueInFlow;
            } else {
              // If exception wasn't the exception to disable default behavior we should bubble up the exception.
              throw e;
            }
          }
        }
      }

      isFormSubmit.value = false;

      if (state.action?.state && defaultBehavior) {
        await state.action?.triggerAction(props.model, props.model.column.replacementTags);
      }
    };

    const buttonInlineStyles = computed<CSSProperties>(() => {
      const store = useStore();
      const campaignState = store.getters['campaign/state'];

      let buttonThickness: number | undefined;

      if (typeof props.model.state.layout?.styles?.normal?.borderWidth === 'undefined') {
        if (
          campaignState.config?.globalLayout &&
          typeof campaignState.globalLayout?.buttons?.borderThickness === 'number'
        ) {
          buttonThickness = campaignState.globalLayout?.buttons?.borderThickness;
        } else if (
          typeof campaignState.layout?.buttons?.borderThickness === 'number' &&
          !campaignState.config?.globalLayout
        ) {
          buttonThickness = campaignState.layout.buttons.borderThickness;
        }
      }

      return {
        ...state.elementStyling.buttonInlineStyle,
        ...state.elementStyling.normal,
        ...(typeof buttonThickness === 'number' && {
          ...(buttonThickness === 0 && {
            borderStyle: 'none'
          }),
          borderWidth: `${buttonThickness}px`
        })
      };
    });

    const doKeybindings = (e: KeyboardEvent) => {
      if (e.key === capitalizeFirstLetter(KeyBinding.ENTER) || e.key === capitalizeFirstLetter(KeyBinding.ESCAPE)) {
        onButtonClick(e);
      }
    };

    const initKeybindings = () => {
      if (!isSsr()) {
        if (
          state.action &&
          state.action.state.keybinding &&
          (state.action.state.keybinding === KeyBinding.ENTER || state.action.state.keybinding === KeyBinding.ESCAPE)
        ) {
          window.addEventListener('keydown', doKeybindings);
        }
      }
    };

    let readyPromiseResolve: (() => void) | undefined;
    const readyPromise = new Promise<void>((resolve) => {
      readyPromiseResolve = resolve;
    });

    onMounted(async () => {
      const { isFontLoaded } = useFontObserver();

      if (state.layout?.styles?.fontFamily) {
        await isFontLoaded(state.layout.styles.fontFamily);
      }
      if (readyPromiseResolve) {
        readyPromiseResolve();
      }
      await state.action?.startAutoTrigger(onButtonClick);
    });

    watch(
      () => state.action?.state?.keybinding,
      () => {
        if (!isSsr()) {
          window.removeEventListener('keydown', doKeybindings);
        }
        initKeybindings();
      },
      { deep: true, immediate: true }
    );

    return {
      state,
      isDisabled,
      buttonInlineStyles,
      onButtonClick,
      onBeforeEnter: async () => {
        await readyPromise;
      }
    };
  }
});
</script>
